import { useMemo, useState } from 'react'
import { Tooltip } from 'antd'
import { useTranslation } from 'react-i18next'
import { Checkbox, Icon, Input, Select, StatusChip } from '@mysaiva/core'
import {
  SCOPE_CATALOG,
  SCOPE_GROUPS,
  SCOPE_GROUP_TONE,
  getScopeGroup
} from '../../api-keys/scopeCatalog'
import type { ScopeGroupId } from '../../api-keys/apiKeys.types'
import styles from './ApiKeys.module.scss'

interface ScopePickerProps {
  selected: string[]
  onChange: (next: string[]) => void
  /** Search input above the group filter (create) or beside it (edit). */
  layout?: 'stacked' | 'inline'
  error?: string
}

/**
 * Scope search, Scope Groups filter and the grouped scope checklist shared
 * by the Create and Edit panels.
 */
export function ScopePicker({ selected, onChange, layout = 'stacked', error }: ScopePickerProps) {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const [group, setGroup] = useState<ScopeGroupId | 'all'>('all')

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase()
    return SCOPE_CATALOG.filter(scope => {
      if (group !== 'all' && scope.group !== group) return false
      if (!needle) return true
      return (
        scope.id.toLowerCase().includes(needle) || scope.description.toLowerCase().includes(needle)
      )
    })
  }, [query, group])

  const toggle = (id: string) => {
    onChange(selected.includes(id) ? selected.filter(s => s !== id) : [...selected, id])
  }

  const search = (
    <div className={styles.scopeSearch}>
      <Input
        allowClear
        value={query}
        onChange={event => setQuery(event.target.value)}
        prefix={<Icon icon="Search" size={20} />}
        maxLength={120}
        placeholder={t('developerPlatform.apiKeys.form.searchScopes')}
        shadow={false}
        data-testid="scope-search"
      />
    </div>
  )

  const groupSelect = (
    <div className={styles.field}>
      <span className={styles.fieldLabel}>{t('developerPlatform.apiKeys.form.scopeGroups')}</span>
      <Select
        value={group}
        onChange={value => setGroup(value as ScopeGroupId | 'all')}
        options={[
          { value: 'all', label: t('developerPlatform.apiKeys.form.allGroups') },
          ...SCOPE_GROUPS.map(g => ({ value: g.id, label: t(g.labelKey) }))
        ]}
        data-testid="scope-group-filter"
      />
    </div>
  )

  return (
    <div className={styles.form} data-testid="scope-picker">
      {layout === 'stacked' ? (
        <>
          {search}
          {groupSelect}
        </>
      ) : (
        <div className={styles.fieldRow}>
          {groupSelect}
          {search}
        </div>
      )}
      <h3 className={styles.sectionTitle}>{t('developerPlatform.apiKeys.form.scopes')}</h3>
      {error && (
        <span className={styles.fieldError} role="alert">
          {error}
        </span>
      )}
      <div
        className={styles.scopeList}
        role="group"
        aria-label={t('developerPlatform.apiKeys.form.scopes')}
      >
        {visible.length === 0 && (
          <div className={styles.scopeEmpty}>{t('developerPlatform.apiKeys.form.noScopes')}</div>
        )}
        {visible.map(scope => {
          const checked = selected.includes(scope.id)
          const group = getScopeGroup(scope.group)
          return (
            <label key={scope.id} className={styles.scopeRow} data-testid={`scope-row-${scope.id}`}>
              <Checkbox
                checked={checked}
                onChange={() => toggle(scope.id)}
                aria-label={scope.id}
                data-testid={`scope-checkbox-${scope.id}`}
              />
              <span className={styles.scopeBody}>
                <span className={styles.scopeId}>{scope.id}</span>
                <span className={styles.scopeMeta}>
                  <span>{scope.description}</span>
                  {/* The chip explains its group on hover (design: "Scope group Description"). */}
                  <Tooltip title={t(group.descriptionKey)} placement="top">
                    <span
                      className={styles.scopeGroupChip}
                      tabIndex={0}
                      data-testid={`scope-group-chip-${scope.id}`}
                    >
                      <StatusChip tone={SCOPE_GROUP_TONE[scope.group]} variant="filled" size="sm">
                        {t(group.labelKey)}
                      </StatusChip>
                    </span>
                  </Tooltip>
                </span>
              </span>
            </label>
          )
        })}
      </div>
    </div>
  )
}
